import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Badge, HStack, Button, VStack, Container, Heading, Tag, Divider,
} from '@chakra-ui/react';
import { FiMapPin, FiClock, FiBriefcase, FiArrowLeft, FiSend, FiCheck } from 'react-icons/fi';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { careerApi } from './careerApi';

const JOB_TYPE_LABEL = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', internship: 'Internship' };

export default function JobDetail() {
  const { req_no } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [req_no]);

  async function load() {
    setLoading(true);
    try {
      const res = await careerApi.jobDetail(req_no);
      setJob(res?.job || null);
    } catch (_) {}
    setLoading(false);
  }

  function apply() {
    // Public apply flow — no login required.
    navigate(`/apply/${req_no}`);
  }

  if (loading) return <Container maxW="1200px" py="48px"><Text color="#94A3B8">Loading…</Text></Container>;
  if (!job) return (
    <Container maxW="1200px" py="48px">
      <Box bg="white" border="1px solid #E5E9F0" borderRadius="16px" p="60px" textAlign="center">
        <Text fontSize="18px" fontWeight="700" mb="8px" color="#0F172A">This position is no longer open</Text>
        <Text fontSize="13px" color="#64748B" mb="20px">The role may have been filled or paused.</Text>
        <Button as={Link} to="/" bg="#1E76CE" color="white" _hover={{ bg: '#1A67B4' }}>Browse open positions</Button>
      </Box>
    </Container>
  );

  const mustSkills = safeParse(job.have_skills);
  const goodSkills = safeParse(job.good_skills);

  return (
    <Box>
      {/* Hero */}
      <Box bgGradient="linear(to-br, #E0F2FF 0%, #F0F9FF 60%, #F7F9FC 100%)" borderBottom="1px solid #E5E9F0" pt="32px" pb="48px">
        <Container maxW="1000px">
          <HStack cursor="pointer" onClick={() => navigate('/')} color="#64748B" _hover={{ color: '#1E76CE' }} mb="20px" spacing="6px">
            <FiArrowLeft /><Text fontSize="13px" fontWeight="600">Back to open positions</Text>
          </HStack>
          <Badge bg="white" color="#1E76CE" fontSize="10px" px="10px" py="4px" borderRadius="full" textTransform="uppercase" letterSpacing="0.5px" fontWeight="700" mb="14px">
            {JOB_TYPE_LABEL[job.job_type] || job.job_type}
          </Badge>
          <Heading as="h1" fontSize={{ base: '28px', md: '40px' }} fontWeight="800" color="#0F172A" lineHeight="1.15" mb="12px">
            {job.title || job.JobRole?.name}
          </Heading>
          <HStack spacing="20px" fontSize="14px" color="#475569" flexWrap="wrap" mb="20px">
            {(job.Team?.name || job.Business_Unit?.name) && (
              <HStack spacing="6px"><FiBriefcase /><Text>{job.Team?.name || job.Business_Unit?.name}</Text></HStack>
            )}
            {job.Location?.name && <HStack spacing="6px"><FiMapPin /><Text>{job.Location.name}</Text></HStack>}
            {job.Shift?.name && <HStack spacing="6px"><FiClock /><Text>{job.Shift.name}</Text></HStack>}
            {job.experience_min != null && <Text>· {job.experience_min}–{job.experience_max ?? '?'} yrs experience</Text>}
          </HStack>
          <HStack spacing="12px" flexWrap="wrap">
            <Button size="lg" bg="#1E76CE" color="white" _hover={{ bg: '#1A67B4' }} leftIcon={<FiSend />} onClick={apply} px="24px" fontWeight="700">
              Apply for this role
            </Button>
            <Text fontSize="12px" color="#94A3B8" fontWeight="600">Ref: {job.req_no}</Text>
          </HStack>
        </Container>
      </Box>

      <Container maxW="1000px" py="40px">
        <Flex gap="32px" alignItems="flex-start" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
          {/* Main body */}
          <Box flex="1" minW="0">
            {job.job_description_html ? (
              <Box className="jd-body" fontSize="15px" color="#334155" lineHeight="1.65"
                sx={{
                  '& h1, & h2, & h3, & h4': { fontWeight: '800', color: '#0F172A', mb: '10px', mt: '24px', letterSpacing: '-0.01em' },
                  '& h1': { fontSize: '24px' }, '& h2': { fontSize: '22px' }, '& h3': { fontSize: '18px' }, '& h4': { fontSize: '16px' },
                  '& p': { mb: '12px' },
                  '& ul, & ol': { pl: '22px', mb: '16px' },
                  '& li': { mb: '6px' },
                  '& strong': { fontWeight: '700', color: '#0F172A' },
                  '& em': { fontStyle: 'italic' },
                  '& code': { bg: '#F1F5F9', px: '6px', py: '2px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px' },
                  '& a': { color: '#1E76CE', textDecoration: 'underline' },
                }}
                dangerouslySetInnerHTML={{ __html: job.job_description_html }} />
            ) : (
              <Text color="#94A3B8" fontSize="14px" mb="24px">Full job description will be shared during the interview.</Text>
            )}

            <VStack align="stretch" spacing="24px" mt="32px">
              <SkillsBlock title="You'll need" skills={mustSkills} tone="must" />
              <SkillsBlock title="Nice to have" skills={goodSkills} tone="good" />
            </VStack>

            <Divider my="32px" borderColor="#E5E9F0" />

            <Box bg="#F0F9FF" border="1px solid #BAE0FF" borderRadius="14px" p="24px 28px">
              <Text fontSize="15px" fontWeight="700" color="#0F172A" mb="6px">Ready to apply?</Text>
              <Text fontSize="13px" color="#475569" mb="16px">
                It takes 2 minutes. You'll create an account, pick the skills you have, and optionally attach a portfolio.
              </Text>
              <Button bg="#1E76CE" color="white" _hover={{ bg: '#1A67B4' }} leftIcon={<FiSend />} onClick={apply} fontWeight="700">
                Apply for this role
              </Button>
            </Box>
          </Box>

          {/* Sidebar */}
          <Box w={{ base: '100%', md: '280px' }} flexShrink={0}>
            <Box bg="white" border="1px solid #E5E9F0" borderRadius="14px" p="20px 22px" position="sticky" top="88px">
              <Text fontSize="12px" color="#94A3B8" textTransform="uppercase" letterSpacing="1px" fontWeight="700" mb="14px">At a glance</Text>
              <VStack align="stretch" spacing="12px" fontSize="13px">
                <MetaRow icon={<FiBriefcase />} label="Team" value={job.Team?.name || job.Business_Unit?.name} />
                <MetaRow icon={<FiMapPin />}    label="Location" value={job.Location?.name} />
                <MetaRow icon={<FiClock />}     label="Shift" value={job.Shift?.name} />
                <MetaRow icon={<FiCheck />}     label="Type" value={JOB_TYPE_LABEL[job.job_type] || job.job_type} />
                {job.experience_min != null && (
                  <MetaRow icon={<FiCheck />} label="Experience" value={`${job.experience_min}–${job.experience_max ?? '?'} yrs`} />
                )}
              </VStack>
              <Button mt="18px" w="full" bg="#1E76CE" color="white" _hover={{ bg: '#1A67B4' }} leftIcon={<FiSend />} onClick={apply} fontWeight="700">
                Apply now
              </Button>
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
}

function SkillsBlock({ title, skills, tone }) {
  if (!skills || skills.length === 0) return null;
  const bg = tone === 'must' ? '#FEF3F2' : '#F0FDF4';
  const color = tone === 'must' ? '#B91C1C' : '#166534';
  return (
    <Box>
      <Text fontSize="15px" fontWeight="800" color="#0F172A" mb="10px">{title}</Text>
      <HStack spacing="6px" wrap="wrap">
        {skills.map((id, i) => (
          <Tag key={i} size="md" bg={bg} color={color} borderRadius="full" px="12px" py="6px" fontWeight="600">
            Skill #{id}
          </Tag>
        ))}
      </HStack>
    </Box>
  );
}

function MetaRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <HStack spacing="10px" alignItems="flex-start">
      <Box color="#94A3B8" mt="2px">{icon}</Box>
      <Box>
        <Text fontSize="11px" color="#94A3B8" textTransform="uppercase" letterSpacing="0.5px" fontWeight="700">{label}</Text>
        <Text fontSize="14px" color="#0F172A" fontWeight="600">{value}</Text>
      </Box>
    </HStack>
  );
}

function safeParse(s) {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch (_) { return []; }
}
